<script setup lang="ts">
import type { ItemKind } from '@satisa-memory/shared'
import { computed } from 'vue'
import { KIND_LABELS } from '../lib/ledger'

/**
 * An item's kind as a solid badge, drawn like a source badge (SourceBadge.vue):
 * which way the next move goes. I owe points right, owed to me left, a
 * follow-up up; a decision has the library's scale.
 */
const props = withDefaults(defineProps<{ kind: ItemKind; size?: number }>(), { size: 20 })

/** Lucide outlines (ISC), on a 24-unit grid: arrow-right, arrow-left, arrow-up, scale. */
const ICONS: Record<ItemKind, string[]> = {
  i_owe: ['M5 12h14', 'm12 5 7 7-7 7'],
  owed_to_me: ['M19 12H5', 'm12 19-7-7 7-7'],
  follow_up: ['M12 19V5', 'm5 12 7-7 7 7'],
  decision: [
    'm16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z',
    'm2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z',
    'M7 21h10',
    'M12 3v18',
    'M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2'
  ]
}

/** Fixed, like the source colours, so the badge reads the same in either theme. */
const COLORS: Record<ItemKind, string> = {
  i_owe: '#4338ca',
  owed_to_me: '#15803d',
  follow_up: '#c2410c',
  decision: '#0f6b5e'
}

const glyph = computed(() => Math.round(props.size * (props.kind === 'decision' ? 0.62 : 0.7)))
</script>

<template>
  <span
    class="kind-mark"
    :style="{ background: COLORS[kind], width: `${size}px`, height: `${size}px` }"
    :title="KIND_LABELS[kind]"
    aria-hidden="true"
    ><svg
      viewBox="0 0 24 24"
      :width="glyph"
      :height="glyph"
      fill="none"
      stroke="currentColor"
      :stroke-width="kind === 'decision' ? 2.4 : 2.8"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path v-for="d in ICONS[kind]" :key="d" :d="d" /></svg
  ></span>
</template>

<style scoped>
.kind-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 5px;
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
}
</style>
